import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { ButtonType, OptionsHelper, Receituario, showAlert, showError } from '_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  FormToggle,
  InfoAlert,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import { termoUsoDoReceituario } from 'app/modules/apps/atendimento_medicos/prescricao/core/receituario'
import { MedicamentoDtoCadastro, MedicamentoView } from '../../core/_models'
import { medicamentoSchema } from './validation/medicamentoSchema'
import { createMedicamento, updateMedicamento } from '../../core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'

export const MedicamentoModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: medicamento } = (fetchStates['medicamento'] || {}) as FetchState<MedicamentoView>

  const initialValues = {
    nome: medicamento?.nome || '',
    codigo: medicamento?.codigo || '',
    substancia: medicamento?.substancia || '',
    concentracao: medicamento?.concentracao || '',
    apresentacao: medicamento?.apresentacao || '',
    classe: medicamento?.classe || '',
    receituario: OptionsHelper.receituarios.find((o) => o.value === medicamento?.receituario) as
      | IOption<Receituario>
      | undefined,
    // A situação não se edita aqui (Inativar/Reativar no menu da lista): vai como está.
    ativo: medicamento ? medicamento.ativo !== false : true,
    lme: medicamento?.lme || false,
    sus: medicamento?.sus || false,
    popular: medicamento?.popular || false,
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => medicamentoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const data: MedicamentoDtoCadastro = {
          nome: values.nome,
          codigo: values.codigo || undefined,
          substancia: values.substancia || undefined,
          concentracao: values.concentracao || undefined,
          apresentacao: values.apresentacao || undefined,
          classe: values.classe || undefined,
          receituario: values.receituario?.value,
          ativo: values.ativo,
          lme: values.lme,
          sus: values.sus,
          popular: values.popular,
        }

        if (medicamento?.id) {
          await updateMedicamento(medicamento.id, data)
        } else {
          await createMedicamento(data)
        }

        await showAlert({
          title: t('common.success'),
          text: medicamento?.id
            ? t('alert.update', { name: t('medicamento.label'), gender: t('medicamento.gender') })
            : t('alert.create', { name: t('medicamento.label'), gender: t('medicamento.gender') }),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const termoUso = termoUsoDoReceituario(formik.values.receituario?.value)

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <div className='col-md-8 mb-3'>
          <FormInput formik={formik} name='nome' entity='nome' isRequired />
        </div>
        <div className='col-md-4 mb-3'>
          <FormInput formik={formik} name='codigo' entity='codigo' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='substancia' entity='substancia' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='concentracao' entity='concentracao' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='apresentacao' entity='apresentacao' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='classe' entity='classe' />
        </div>
        <div className='col-md-6 mb-3'>
          <FormSelect
            formik={formik}
            name='receituario'
            entity='receituario'
            inputId='medicamento-admin-receituario'
            options={OptionsHelper.receituarios}
            isDisabled={formik.isSubmitting}
            isClearable={false}
            isRequired
          />
        </div>
      </div>

      {termoUso && (
        <InfoAlert variant='info' role='note' className='mb-3'>
          {t('medicamento.exige_termo', { termo: termoUso })}
        </InfoAlert>
      )}

      <div className='d-flex flex-wrap gap-4 mb-3'>
        <FormToggle formik={formik} name='lme' entity='lme' />
        <FormToggle formik={formik} name='sus' entity='sus' />
        <FormToggle formik={formik} name='popular' entity='popular' />
      </div>

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
